/**
 * error.ts
 *
 * DB Models
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

export enum ErrorCode {
  basic = 0,
  unauth = 1,  // don't have permissions for this operation
  network = 2, // iOS network issues
  revoked = 3, // token was revoked

  expired = 20, // token expired
  notYet = 21, // token/invite isn't valid yet
  claimed = 22, // token/invite already claimed

  // GroupError codes
  group = 1000,  // generic group error
  collision = 1001,  // messages collided, can try again
  badMsg = 1002,  // Msg contains invalid data
  continueAck = 1003, // while processing messages,
                      // indicates that msgs should be ACKed and cleared
  mustPause = 1004, // while processing messages,
                    // indicates that processing must stop and wait for
                    // another group member to acknowledge an operation
  sync = 1010  // Out-Of-Sync error due to mismatching cryptographic info
               // or invalid msgs/group parameters
}

export class AbortError extends Error {}

export class HttpError extends Error {
  constructor(message: string, public statusCode: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = this.constructor.name;
  }
}

export class UnauthError extends HttpError {
  constructor(message: string,
      public statusCode: number,
      public code = ErrorCode.group) {
    super(message, statusCode);
  }
}

export class GroupError extends HttpError {
  constructor(message: string,
      public statusCode: number,
      public code = ErrorCode.group) {
    super(message, statusCode);
  }
}

export class ExpiredTokenError extends HttpError {
  constructor(message: string,
      statusCode: number,
      public code = ErrorCode.expired) {
    super(message, statusCode);
  }
}
export class EarlyEnrollmentError extends HttpError {
  constructor(message: string,
      statusCode: number,
      public code = ErrorCode.notYet) {
    super(message, statusCode);
  }
}
export class AlreadyClaimedError extends HttpError {
  constructor(message: string,
      statusCode: number,
      public code = ErrorCode.notYet) {
    super(message, statusCode);
  }
}


export class RecoveryTooOldError extends Error {}

export interface ErrorInfo {
  message: string,
  line?: number,
  column?: number,
  error: string,
  level: string
}

export function errorFor(respBody: any, statusCode: number,
    defaultMsg = "Failed Communicating with Server"): HttpError {
  if (!respBody) {
    return new HttpError(defaultMsg, statusCode);
  }
  const code = respBody.code || ErrorCode.basic;
  const message = respBody.message || defaultMsg;

  switch(code) {
    case ErrorCode.unauth:
    case ErrorCode.revoked:
      return new UnauthError(message, statusCode, code);
    case ErrorCode.group:
    case ErrorCode.collision:
    case ErrorCode.badMsg:
    case ErrorCode.sync:
      return new GroupError(message, statusCode, code);
    case ErrorCode.expired:
      return new ExpiredTokenError(message, statusCode, code);
    case ErrorCode.notYet:
      return new EarlyEnrollmentError(message, statusCode, code);
    case ErrorCode.claimed:
      return new AlreadyClaimedError(message, statusCode, code);
    default:
      return new HttpError(message, statusCode);
  }
}
